import { useEffect, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  generatePath,
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  ButtonPrimary,
  ButtonPseudo,
  PermissionEnum,
  Popup,
  ResponseError,
  PermissionDenied,
  responseErrorsResolver,
  SidePanelType,
  getResponseErrors,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  changePanelState,
  clearSidepanelMarginState,
  setMarginIsCustom,
  setMarginId,
} from './store/slices/sidepanel';
import {
  useLazyGetDefaultMarginQuery,
  useUpdateDefaultMarginMutation,
  useCreateAmountsMutation,
  useLazyGetMarginQuery,
  useUpdateMarginMutation,
  useLazyGetMarginsQuery,
  useRestoreToDefaultMutation,
  useUpdateAmountMutation,
} from './store/services';
import MarginPanelContent from '../../components/margin-panel/margin-panel-content';
import { MarginFormValues, MarginAmount, MarginItem } from './types';

import ROUTES from '../../routes';

const MarginDetails = (): React.JSX.Element => {
  const {
    sidepanel: { panelIsOpen, marginValues },
    list: { defaultMargin },
  } = useAppSelector((state) => state.margins);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const { activePermissions } = useAppSelector(
    (state) => state.exchangeDictionaries,
  );
  const [defaultAmounts, setDefaultAmounts] = useState<MarginAmount[]>([]);
  const [amountsList, setAmounts] = useState<MarginAmount[]>();
  const [initialMarginAmounts, setInitialMarginAmounts] = useState<
    MarginAmount[]
  >([]);
  const [updateIsSuccess, setUpdateIsSuccess] = useState(false);
  const [marginFeeError, setMarginFeeError] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [restoreConfirmationPopupVisible, setRestoreConfrimationPopupVisible] =
    useState(false);

  const { currency, marginId } = useParams();

  const { search } = useLocation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = useForm<MarginFormValues>();

  const amounts = watch('amounts');
  const currencyFromId = watch('currencyFromId');

  const [
    getDefaultMargin,
    { isLoading: defaultMarginIsLoading, isFetching: defaultIsFetching },
  ] = useLazyGetDefaultMarginQuery();
  const [getMargin, { isLoading: marginIsLoading, isFetching }] =
    useLazyGetMarginQuery();
  const [updateDefaultMargin, { isLoading: requestInProgress, isSuccess }] =
    useUpdateDefaultMarginMutation();
  const [updateMargin, { isLoading: updateInProgress }] =
    useUpdateMarginMutation();
  const [
    createAmounts,
    { isLoading: createAmountsInProgress, isSuccess: createAountsIsSuccess },
  ] = useCreateAmountsMutation();
  const [getMargins] = useLazyGetMarginsQuery();
  const [updateAmount] = useUpdateAmountMutation();
  const [restore, { isLoading: restoreInProgress }] =
    useRestoreToDefaultMutation();

  const fields = watch();

  const handleClose = useCallback(() => {
    dispatch(clearSidepanelMarginState());

    setTimeout(() => {
      const url = generatePath(ROUTES.MARGINS_CURRENCY, {
        currency: currency as string,
      });

      navigate(`${url}${search || ''}`);
    }, 100);
  }, [dispatch, search]);

  let marginsIsEqual: boolean;

  if (marginId === 'all') {
    marginsIsEqual = isEqual(
      {
        targetAccountGainsId: fields.targetAccountGainsId,
        targetAccountLossesId: fields.targetAccountLossesId,
        amounts: fields.amounts,
      },
      {
        targetAccountGainsId: marginValues?.targetAccountGainsId,
        targetAccountLossesId: marginValues?.targetAccountLossesId,
        amounts:
          marginValues?.amounts?.edges.map(
            (item: { node: MarginAmount }) => item.node,
          ) || [],
      },
    );
  } else {
    marginsIsEqual = isEqual(
      {
        targetAccountGainsId: fields.targetAccountGainsId,
        targetAccountLossesId: fields.targetAccountLossesId,
        amounts: fields.amounts,
        tariffId: fields.tariffId,
        rateSourceId: fields.rateSourceId,
      },
      {
        targetAccountGainsId: marginValues?.targetAccountGainsId,
        targetAccountLossesId: marginValues?.targetAccountLossesId,
        amounts:
          marginValues?.amounts?.edges.map(
            (item: { node: MarginAmount }) => item.node,
          ) || [],
        tariffId: marginValues?.tariffId,
        rateSourceId: marginValues?.rateSourceId,
      },
    );
  }

  const getMarginDetails = useCallback(async (id: string) => {
    const response = (await getMargin(id)) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList: setGlobalError,
      });
    }
  }, []);

  const getDefaultMarginAction = useCallback(async () => {
    const response = (await getDefaultMargin()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList: setGlobalError,
      });
    }
  }, []);

  const onFormSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const newAmounts = values?.amounts
      ?.filter((amount) =>
        defaultAmounts.every((item) => amount.id !== item.id),
      )
      .filter(
        (item) => item.feePercent.toString() && item.fromAmount.toString(),
      );

    const updatedAmounts = values.amounts?.filter((amount) =>
      defaultAmounts.every((item) => !isEqual(amount, item)),
    );

    let response;

    if (marginId === 'all') {
      if (!marginsIsEqual) {
        await updateDefaultMargin({
          body: {
            targetAccountGainsId: fields.targetAccountGainsId,
            targetAccountLossesId: fields.targetAccountLossesId,
          },
        });
      }

      if (newAmounts.length) {
        // eslint-disable-next-line no-useless-assignment
        response = (await createAmounts({
          body: {
            items: newAmounts.map((item: MarginAmount) => ({
              feePercent: Number(item.feePercent),
              fromAmount: Number(item.fromAmount),
              defaultMarginId: marginValues?.id,
            })),
          },
        })) as { error: ResponseError };
      } else {
        setUpdateIsSuccess(true);
      }
    } else {
      response = (await updateMargin({
        id: marginId as string,
        body: {
          rateSourceId: values.rateSourceId,
          currencyFromId: values.currencyFromId,
          currencyToId: values.currencyToId,
          tariffId: values.tariffId,
          targetAccountGainsId: fields.targetAccountGainsId,
          targetAccountLossesId: fields.targetAccountLossesId,
        },
      })) as { error: ResponseError };

      if (newAmounts.length) {
        response = await createAmounts({
          body: {
            items: newAmounts.map((item: MarginAmount) => ({
              feePercent: Number(item.feePercent),
              fromAmount: Number(item.fromAmount),
              marginId: marginId as string,
            })),
          },
        });

        dispatch(setMarginIsCustom());
      } else {
        setUpdateIsSuccess(true);
      }

      if (!(response as { error: ResponseError }).error) {
        setUpdateIsSuccess(true);

        setTimeout(() => {
          let queryValue = `?take=20&currencyFromId=${currency}`;

          if (currency) {
            queryValue = `${queryValue}${
              search ? search.replace('?', '&') : ''
            }`;
          }

          getMargins(queryValue);
        }, 300);
      } else if ((response as { error: ResponseError }).error) {
        responseErrorsResolver({
          errors: (response as { error: ResponseError }).error,
          setError,
          setGlobalError,
        });

        if (
          (response as { error: ResponseError }).error.data?.message[0] &&
          (response as { error: ResponseError }).error.data.message[0] // @ts-ignore
            ?.property === 'coreError'
        ) {
          const error = // @ts-ignore
            (response as { error: ResponseError }).error.data.message[0]?.value;

          if (error && error.includes('Float')) {
            setMarginFeeError('Fee must be a number');
          }
        }
      }
    }

    if (
      updatedAmounts.length &&
      (marginValues?.isCustom || marginId === 'all')
    ) {
      await Promise.all(
        updatedAmounts
          .filter((item) => !item?.id?.includes('amount'))
          .map(async (item) => {
            await updateAmount({
              id: item.id as string,
              body: {
                fromAmount: item.fromAmount,
                feePercent: item.feePercent,
              },
            });
          }),
      );
    }
  });

  const onRestoreToDefault = async () => {
    const response = restore(marginValues?.id as string) as {
      data?: { data: MarginItem };
      error?: ResponseError;
    };

    if (response && !(response as { error: ResponseError }).error) {
      setTimeout(() => {
        let queryValue = `?take=20&currencyFromId=${currency}`;

        if (currency) {
          queryValue = `${queryValue}${search ? search.replace('?', '&') : ''}`;
        }

        getMargins(queryValue);
        handleClose();
      }, 300);
    } else if ((response as { error: ResponseError }).error) {
      responseErrorsResolver({
        errors: (response as { error: ResponseError }).error,
        setError,
        setGlobalError,
      });
    }
  };

  const onResetMargins = () => {
    setRestoreConfrimationPopupVisible(false);

    reset({
      ...fields,
      targetAccountGainsId: fields.targetAccountGainsId,
      targetAccountLossesId: fields.targetAccountLossesId,
      amounts: defaultMargin?.amounts?.edges.map(
        (item: { node: MarginAmount }) => item.node,
      ),
    });

    setAmounts(
      defaultMargin?.amounts?.edges
        .map((item: { node: MarginAmount }) => item.node)
        .sort((a, b) => a.fromAmount - b.fromAmount),
    );

    onRestoreToDefault();
  };

  useEffect(() => {
    dispatch(changePanelState(true));
    dispatch(setMarginId(marginId as string));

    return () => {
      dispatch(clearSidepanelMarginState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (marginId !== 'all' && panelIsOpen) {
      getMarginDetails(marginId as string);
    }
  }, [dispatch, panelIsOpen, marginId]);

  useEffect(() => {
    if (
      marginValues?.amounts &&
      marginValues.amounts.edges.length &&
      !amountsList?.length
    ) {
      setAmounts(
        marginValues?.amounts.edges
          .map((item: { node: MarginAmount }) => item.node)
          .sort(
            (a: MarginAmount, b: MarginAmount) => a.fromAmount - b.fromAmount,
          ),
      );
    } else if (
      marginValues?.amounts &&
      !marginValues?.amounts?.edges?.length &&
      !amountsList?.length
    ) {
      setAmounts([
        {
          marginId: 'margin-0',
          fromAmount: 0,
          feePercent: 0,
          id: 'amount-0',
        },
      ]);
    }
  }, [marginValues?.amounts, amountsList]);

  useEffect(() => {
    if (marginId === 'all') {
      getDefaultMarginAction();
    }
  }, [marginId]);

  useEffect(() => {
    if (marginValues) {
      let body: MarginFormValues = {
        targetAccountGainsId:
          marginValues.targetAccountGainsId ||
          (defaultMargin?.targetAccountGainsId as string) ||
          '',
        targetAccountLossesId:
          marginValues.targetAccountLossesId ||
          (defaultMargin?.targetAccountLossesId as string) ||
          '',
        amounts:
          marginValues?.amounts?.edges.map(
            (item: { node: MarginAmount }) => item.node,
          ) || [],
      };

      setInitialMarginAmounts(
        marginValues?.amounts?.edges.map(
          (item: { node: MarginAmount }) => item.node,
        ) || [],
      );

      if (marginId !== 'all') {
        body = {
          ...body,
          rateSourceId: marginValues.rateSourceId,
          currencyFromId: marginValues.currencyFromId,
          currencyToId: marginValues.currencyToId,
          tariffId: marginValues.tariffId,
        };
      }

      reset(body);

      setDefaultAmounts(
        !marginValues.isCustom && marginId !== 'all'
          ? []
          : (marginValues?.amounts?.edges.map(
              (item) => item.node,
            ) as MarginAmount[]) || [],
      );
    }
  }, [marginValues, marginId]);

  useEffect(() => {
    if (updateIsSuccess || createAountsIsSuccess) {
      handleClose();
    }
  }, [isSuccess, updateIsSuccess]);

  let titleText: string;

  if (marginId === 'all') {
    titleText = 'General FX margin';
  } else if (
    defaultMarginIsLoading ||
    marginIsLoading ||
    isFetching ||
    defaultIsFetching
  ) {
    titleText = 'Loading...';
  } else {
    titleText = `${marginValues?.currencyFrom?.name}-${marginValues?.currencyTo?.name} FX margin`;
  }

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_EXCHANGE_EDIT_MARGINS_ACTION,
    ) &&
    marginId === 'all'
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <MarginPanelContent
        control={control}
        errors={errors}
        isLoading={
          defaultMarginIsLoading ||
          marginIsLoading ||
          isFetching ||
          defaultIsFetching
        }
        amounts={amountsList}
        setValue={setValue}
        isEditable={activePermissions?.includes(
          PermissionEnum.SETTINGS_EXCHANGE_EDIT_MARGINS_ACTION,
        )}
        isCustom={marginValues?.isCustom}
        setAmounts={setAmounts}
        isDefaultPanel={marginId === 'all'}
        onResetMargins={setRestoreConfrimationPopupVisible}
        marginId={marginId}
        marginFeeError={marginFeeError}
        auditLogAvailable={activePermissions?.includes(
          PermissionEnum.AUDIT_LOG_LIST,
        )}
        restoreInProgress={restoreInProgress}
        currencyFromId={currencyFromId}
      />
    );
  }

  return (
    <>
      <SidePanel
        closeOnClickOutside
        isOpen={panelIsOpen}
        type={SidePanelType.Exchange}
        onClose={
          isDirty ||
          (marginId === 'all' && !isEqual(defaultAmounts, amounts)) ||
          (marginId &&
            marginId !== 'all' &&
            !isEqual(initialMarginAmounts, amounts))
            ? () => setDirtyPopupVisible(true)
            : handleClose
        }
        title={titleText}
        errorSidepanel={globalError}
        sidepanelExpandedKey="margin-details"
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_EXCHANGE_EDIT_MARGINS_ACTION,
            ) && (
              <ButtonPrimary
                text={
                  requestInProgress || updateInProgress || restoreInProgress
                    ? 'Loading...'
                    : 'Save'
                }
                onClick={onFormSubmit}
                disabled={
                  requestInProgress ||
                  updateInProgress ||
                  createAmountsInProgress ||
                  marginsIsEqual
                }
                bold
              />
            )}

            <ButtonPseudo
              text="Cancel"
              onClick={
                isDirty ||
                (marginId === 'all' && !isEqual(defaultAmounts, amounts)) ||
                (marginId &&
                  marginId !== 'all' &&
                  !isEqual(initialMarginAmounts, amounts))
                  ? () => setDirtyPopupVisible(true)
                  : handleClose
              }
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <Popup
        title="Are you sure you want to restore to default this margin?"
        onCancelClick={() => setRestoreConfrimationPopupVisible(false)}
        onOkClick={onResetMargins}
        cancelButtonText="Cancel"
        okButtonText="Yes, restore"
        isVisible={restoreConfirmationPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default MarginDetails;
